import { Inject, Injectable, NgZone, Renderer2, forwardRef } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import html2pdf from 'html2pdf.js';
import { DownloadProgressBarDialogComponent } from "../../../shared/download-progress-bar-dialog/download-progress-bar-dialog";

@Injectable()
export class ExportPdfButtonService {

    constructor(
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog,
        @Inject(forwardRef(() => NgZone)) private zone: NgZone
    ) {
    }

    localExportPdf(fileName: string, document: Document, renderer: Renderer2): void {
        let contents = document.getElementsByClassName("mat-tab-body-active");
        if (!contents?.length) {
            contents = document.getElementsByClassName("content");
        }
        if (contents?.length > 0) {
            const clonedNode = contents[0].cloneNode(true);
            renderer.addClass(clonedNode, "export-pdf");
            renderer.setAttribute(clonedNode, "id", "local-export-pdf-container");
            const width = contents[0].clientWidth + 10;
            const height = (contents[0].clientWidth + 10) * 1.4142; // A4 ratio
            const opt = {
                margin: [height / 200, 0, (height / 200) * 5, 0],
                filename: fileName,
                html2canvas: {
                    scale: 2,   // must be scale > 1
                    dpi: 300,
                    scrollY: -window.scrollY,
                    scrollX: -window.scrollX,
                    useCORS: true,
                    onclone: function (documentClone) {
                        // waits rendering
                        return new Promise<void>((resolve) => {
                            setTimeout(() => {
                                resolve();
                            }, 1000)
                        })
                    }
                },
                jsPDF: { unit: 'px', format: [width, height], hotfixes: ["px_scaling"] }
            };
            contents[0].appendChild(clonedNode);
            const clonedElement = document.getElementById("local-export-pdf-container");
            clonedElement.remove();
            this.fixElements(clonedElement).then(() => {
                const dialogRef = this.openDialog();
                setTimeout(() => {
                    html2pdf().from(clonedElement).set(opt).toPdf().get('pdf').then(pdf => {
                        this.addNumberOfPages(pdf);
                        this.zone.run(() => dialogRef.close());
                    }).save().catch(err => {
                        console.error(err);
                        this.zone.run(() => dialogRef.close());
                    });
                }, 100);
            });
        } else {
            console.error("No element to export in PDF");
        }
    }

    private addNumberOfPages(pdf): void {
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(pdf.internal.pageSize.getWidth() / 80);
            pdf.text(i + '/' + totalPages, (pdf.internal.pageSize.getWidth() / 2), (pdf.internal.pageSize.getHeight() - (pdf.internal.pageSize.getHeight() / 100)));
        }
    }

    private openDialog(): any {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '10%';
        dialogConfig.disableClose = true;
        dialogConfig.panelClass = "export-pdf-dialog";
        dialogConfig.data = { title: "Exporting PDF" };
        return this.dialog.open(DownloadProgressBarDialogComponent, dialogConfig);
    }

    private getBase64ImageFromUrl(imageUrl): Promise<any> {
        return fetch(imageUrl).then(res => {
            return res.blob().then(blob => {
                return new Promise((resolve, reject) => {
                    var reader = new FileReader();
                    reader.addEventListener("load", function () {
                        resolve(reader.result);
                    }, false);
                    reader.onerror = () => {
                        return reject();
                    };
                    reader.readAsDataURL(blob);
                })
            });
        })
    }

    private fixElements(clonedElement: HTMLElement): Promise<any> {
        this.fixCanvasElements(clonedElement);
        this.fixMetricStateSummaryTable(clonedElement);
        return this.fixImages(clonedElement);
    }

    private fixImages(clonedElement: HTMLElement): Promise<any> {
        let images: HTMLCollectionOf<Element> = clonedElement.getElementsByTagName('img');
        let promises = [];
        if (images.length) {
            Array.from(images).forEach(image => {
                promises.push(
                    this.getBase64ImageFromUrl(image['src']).then(result => {
                        image.setAttribute('src', result);
                    }).catch(err => console.error(err))
                );
            });
        }
        return Promise.all(promises);
    }

    private fixCanvasElements(clonedElement: HTMLElement): void {
        const origianlCanvases = document.getElementsByTagName('canvas');
        Array.from(clonedElement.getElementsByTagName('canvas')).forEach((cloned, index) => {
            const original = origianlCanvases[index];
            const img = document.createElement('img');
            img.src = original.toDataURL();
            img.width = original.clientWidth;
            img.height = original.height;
            img.style.cssText = original.style.cssText;
            cloned.replaceWith(img);
        });
    }

    private fixMetricStateSummaryTable(clonedElement: HTMLElement): void {
        const divElements = clonedElement.querySelectorAll<HTMLElement>('.am5-html-container>div');
        divElements.forEach(el => el.style.overflow = 'visible');
    }

}